import React, { useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { Modal, ModalContent, ModalHeader } from '../shared/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { CHANGELOG, markUpdatesSeen } from '../../config/changelog';

interface WhatsNewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TYPE_STYLES = {
  feature: { label: 'New', bg: 'bg-emerald-500/20', text: 'text-emerald-400', bgLight: 'bg-emerald-100', textLight: 'text-emerald-700' },
  improvement: { label: 'Improved', bg: 'bg-blue-500/20', text: 'text-blue-400', bgLight: 'bg-blue-100', textLight: 'text-blue-700' },
  fix: { label: 'Fixed', bg: 'bg-orange-500/20', text: 'text-orange-400', bgLight: 'bg-orange-100', textLight: 'text-orange-700' },
} as const;

export const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ isOpen, onClose }) => {
  const { darkMode } = useTheme();

  useEffect(() => {
    if (isOpen) {
      markUpdatesSeen();
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <ModalContent>
        <ModalHeader
          title="What's New"
          icon={
            <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center ${
              darkMode ? 'bg-violet-500/20' : 'bg-violet-100'
            }`}>
              <Sparkles size={24} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
            </div>
          }
        />

        <div className="space-y-5 max-h-[50vh] overflow-y-auto pr-1">
          {CHANGELOG.map((entry) => (
            <div key={entry.version}>
              <div className="flex items-baseline gap-2 mb-2">
                <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  v{entry.version}
                </span>
                <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                  {entry.date}
                </span>
              </div>
              <ul className="space-y-1.5">
                {entry.items.map((item, idx) => {
                  const style = TYPE_STYLES[item.type];
                  return (
                    <li key={idx} className="flex items-start gap-2">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider flex-shrink-0 mt-0.5 ${
                        darkMode ? `${style.bg} ${style.text}` : `${style.bgLight} ${style.textLight}`
                      }`}>
                        {style.label}
                      </span>
                      <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {item.text}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-5">
          <button
            onClick={onClose}
            className={`w-full px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
              darkMode
                ? 'bg-white/10 hover:bg-white/20 text-white'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
            }`}
          >
            Close
          </button>
        </div>
      </ModalContent>
    </Modal>
  );
};
